import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { Body1, FormikTextInput, KeyboardSafeAreaScrollView, useTheme } from "capsule"
import { Formik } from "formik"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, TouchableOpacity, View } from "react-native"
import { Text } from "react-native-paper"
import * as Yup from "yup"

import SubmitButton from "../../components/SubmitButton"
import { INPUT_HEIGHT } from "../../features/config/Constants"
import { NewClientOnboardingNS } from "../../features/i18n/constants"
import { NewClientOnboardingParamList } from "../../features/Navigation/types"
import { getNewClientDocId, updateNewClientData } from "../../features/services/NewClientService"

interface FormValues {
  proName: string
}

const NewClientPatientRecommendationScreen = () => {
  const { t } = useTranslation(NewClientOnboardingNS)
  const navigation =
    useNavigation<
      StackNavigationProp<NewClientOnboardingParamList, "NewClientPatientRecommendation">
    >()

  const [selectedAnswer, setSelectedAnswer] = useState<"yes" | "no" | null>(null)

  const {
    colors: {
      surface: { background },
      primary,
    },
    dimensions: { spacing },
  } = useTheme()

  const s = useMemo(
    () => ({
      container: [
        styles.container,
        {
          backgroundColor: background,
        },
      ],
      innerContent: {
        paddingBottom: spacing * 2,
        paddingHorizontal: spacing * 2,
        gap: spacing * 2,
      },
      radioGroup: [
        styles.radioGroup,
        {
          gap: spacing,
          marginVertical: spacing,
        },
      ],
      radioItem: [
        styles.radioItem,
        {
          borderColor: primary,
          borderRadius: spacing,
          paddingVertical: spacing / 2,
        },
      ],
      radioItemSelected: {
        backgroundColor: `${primary}15`,
        borderWidth: 2,
      },
      input: [
        styles.input,
        {
          paddingLeft: spacing / 4,
          marginVertical: spacing,
        },
      ],
    }),
    [background, spacing, primary],
  )

  const validationSchema = useMemo(
    () =>
      Yup.object({
        proName: Yup.string().required(t("errors.required")),
      }),
    [t],
  )

  const initialValues: FormValues = {
    proName: "",
  }

  const handleNo = async () => {
    setSelectedAnswer("no")
    const docId = await getNewClientDocId()
    await updateNewClientData(docId, { recommendedByPro: false })
    navigation.navigate("NewClientPatientPathologyIntro")
  }

  const onSubmit = async (values: FormValues) => {
    const docId = await getNewClientDocId()
    await updateNewClientData(docId, {
      recommendedByPro: true,
      proName: values.proName,
    })
    navigation.navigate("NewClientPatientPathologyIntro")
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {() => (
        <KeyboardSafeAreaScrollView style={s.container} contentContainerStyle={s.innerContent}>
          <Body1 style={styles.bold}>{t("patientRecommendation.question")}</Body1>

          <View style={s.radioGroup}>
            <TouchableOpacity
              style={[s.radioItem, selectedAnswer === "yes" && s.radioItemSelected]}
              onPress={() => setSelectedAnswer("yes")}
              testID="button_yes"
            >
              <Text style={styles.bold}>{t("patientRecommendation.yes")}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[s.radioItem, selectedAnswer === "no" && s.radioItemSelected]}
              onPress={handleNo}
              testID="button_no"
            >
              <Text style={styles.bold}>{t("patientRecommendation.no")}</Text>
            </TouchableOpacity>
          </View>

          {selectedAnswer === "yes" && (
            <>
              <FormikTextInput
                style={s.input}
                name="proName"
                label={t("patientProName.label")}
                placeholder={t("patientProName.placeholder")}
                testID="input_pro_name"
              />

              <SubmitButton label={t("patientProName.submit")} />
            </>
          )}
        </KeyboardSafeAreaScrollView>
      )}
    </Formik>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  radioGroup: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  radioItem: {
    flex: 1,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    height: 120,
  },
  bold: {
    fontWeight: "bold",
  },
  input: {
    height: INPUT_HEIGHT,
    minHeight: INPUT_HEIGHT,
    borderRadius: 0,
  },
})

export default NewClientPatientRecommendationScreen
